import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { gql, useMutation } from '@apollo/client';
import { accountSelectedSelector } from 'atoms';
import { useMessages } from 'hooks';
import { UserAndContact } from 'types/UsersAndContact.types';
import { TFunction } from 'react-i18next';
import { useYupObject } from 'hooks';

const ADD_CONTACT = gql`
  mutation AddContact(
    $masterCompanyId: Int
    $isCloud: Boolean!
    $accountId: Int!
    $token: String!
    $contact: UserAndContactInput!
    $webService: String
  ) {
    addContact(
      masterCompanyId: $masterCompanyId
      isCloud: $isCloud
      accountId: $accountId
      token: $token
      contact: $contact
      webService: $webService
    ) {
      id
    }
  }
`;

const UPDATE_CONTACT = gql`
  mutation UpdateContact(
    $masterCompanyId: Int
    $isCloud: Boolean!
    $accountId: Int!
    $token: String!
    $contact: UserAndContactInput!
    $webService: String
  ) {
    updateContact(
      masterCompanyId: $masterCompanyId
      isCloud: $isCloud
      accountId: $accountId
      token: $token
      contact: $contact
      webService: $webService
    ) {
      id
    }
  }
`;

function useSubmitContactForm({
  t,
  setOpen,
  refetchContacts,
}: {
  t: TFunction<('contacts' | '_common' | 'forms')[], undefined>;
  setOpen: React.Dispatch<React.SetStateAction<UserAndContact | boolean>>;
  refetchContacts: () => void;
}) {
  const yup = useYupObject();

  const contactReason = yup.object({
    id: yup.number().required().moreThan(0),
    enabled: yup.boolean().required(),
  });
  const validationSchema = yup.object({
    name: yup.string().trim().required(),
    phone: yup.string().trim().phone().required(),
    additionalPhone: yup.string().trim().phone(),
    email: yup.string().email(),
    questionPassword: yup.string().trim(),
    answerPassword: yup.string().trim(),
    answerCoercion: yup.string().trim(),
    taxpayerIdentification: yup.string().trim(),
    identificationNumber: yup.string().trim(),
    function: yup
      .object({
        id: yup.number().required(),
        name: yup.string().required(),
      })
      .nullable(),
    contactsReason: yup
      .object({
        fire: contactReason,
        violation: contactReason,
        medical: contactReason,
        panic: contactReason,
      })
      .test('show_alert', t('contacts:select_a_reason_to_call_this_contact'), (contactsReason) =>
        Object.values(contactsReason).some((reason) => reason.enabled),
      ),
  });

  const accountSelected = useRecoilValue(accountSelectedSelector);
  const { enqueueError, enqueueSuccess } = useMessages();

  const [addContact, { loading: loadingAdd }] = useMutation<{
    addContact: { id: number };
  }>(ADD_CONTACT, {
    variables: {
      ...accountSelected,
      token: '0',
      accountId: accountSelected?.id || 0,
    },
    onCompleted: (data) => {
      if (!data?.addContact?.id) {
        return enqueueError(t('contacts:error_adding_contact'));
      }
      enqueueSuccess(t('contacts:contact_saved'));
      setOpen(false);
      refetchContacts();
    },
    onError: () => {
      enqueueError(t('contacts:error_adding_contact'));
    },
  });

  const [updateContact, { loading: loadingUpdate }] = useMutation<{
    updateContact: { id: number };
  }>(UPDATE_CONTACT, {
    variables: {
      ...accountSelected,
      token: '0',
      accountId: accountSelected?.id || 0,
    },
    onCompleted: (data) => {
      if (!data?.updateContact?.id) {
        return enqueueError(t('contacts:error_updating_contact'));
      }
      enqueueSuccess(t('contacts:contact_updated'));
      setOpen(false);
      refetchContacts();
    },
    onError: () => {
      enqueueError(t('contacts:error_updating_contact'));
    },
  });

  const onSubmit = useCallback(
    (values: UserAndContact) => {
      const contact = normalizeData(validationSchema.cast(values) as UserAndContact);
      if (contact?.id) {
        return updateContact({
          variables: {
            contact,
          },
        });
      }

      return addContact({
        variables: {
          contact,
        },
      });
    },
    [addContact, updateContact, validationSchema],
  );

  return {
    loading: loadingAdd || loadingUpdate,
    onSubmit,
    validationSchema,
  };
}

export function normalizeData(contact: UserAndContact) {
  function unmaskNumber(number: string) {
    return number.replace(/\D/g, '');
  }
  const { partitions, contactsReason, phone, additionalPhone, taxpayerIdentification } =
    contact || {};

  return {
    ...contact,
    phone: phone ? unmaskNumber(phone) : '',
    additionalPhone: additionalPhone ? unmaskNumber(additionalPhone) : '',
    __typename: undefined,
    partitions: partitions?.map((partition) => ({
      ...partition,
      __typename: undefined,
    })),
    type: undefined,
    taxpayerIdentification: taxpayerIdentification ? unmaskNumber(taxpayerIdentification) : '',
    function: contact.function
      ? {
          ...contact.function,
          __typename: undefined,
        }
      : undefined,
    contactsReason: {
      fire: { ...contactsReason.fire, __typename: undefined },
      violation: { ...contactsReason.violation, __typename: undefined },
      medical: { ...contactsReason.medical, __typename: undefined },
      panic: { ...contactsReason.panic, __typename: undefined },
      __typename: undefined,
    },
  };
}

export default useSubmitContactForm;
